import VideoControls from '@utils/videoControls';

/**
 * Manages media loading states for the split hero section.
 * Applies a stencil (pulsating placeholder) to each panel while media loads,
 * then smoothly transitions the media in once ready.
 */
export default class SplitHero {
  /** @param {HTMLElement} sectionElement - The section root element */
  constructor(sectionElement) {
    this.moduleEl = sectionElement;
    this.panels = [
      ...sectionElement.querySelectorAll('.module-split-hero__panel'),
    ];
    this.init();
  }

  /**
   * Initialises each panel — attaches media load listeners or
   * immediately marks panels without media as ready.
   */
  init() {
    this.panels.forEach((panel) => {
      const mediaContainers = [
        ...panel.querySelectorAll('.module-split-hero__media'),
      ];

      if (mediaContainers.length === 0) {
        this.reveal(panel);
        return;
      }

      mediaContainers.forEach((container) =>
        this.observeMedia(container, panel)
      );
      this.initVideoControls(panel);
    });
  }

  /**
   * Attaches the appropriate load listener to the media element
   * (image or video) within a media container.
   * @param {HTMLElement} mediaContainer - The .module-split-hero__media wrapper
   * @param {HTMLElement} panel - The parent panel element
   */
  observeMedia(mediaContainer, panel) {
    const imageElement = mediaContainer.querySelector('img');
    const videoElement = mediaContainer.querySelector('video');

    if (videoElement) {
      if (videoElement.readyState >= 3) {
        this.reveal(panel);
      } else {
        videoElement.addEventListener(
          'canplaythrough',
          () => this.reveal(panel),
          { once: true }
        );
      }
      return;
    }

    if (imageElement) {
      if (imageElement.complete && imageElement.naturalWidth > 0) {
        this.reveal(panel);
      } else {
        imageElement.addEventListener('load', () => this.reveal(panel), {
          once: true,
        });
        imageElement.addEventListener('error', () => this.reveal(panel), {
          once: true,
        });
      }
    }
  }

  /**
   * Removes the stencil state and triggers the media fade-in.
   * @param {HTMLElement} panel - The panel to reveal
   */
  reveal(panel) {
    panel.classList.remove('Stencil');
    panel.classList.add('module-split-hero__panel--loaded');
  }

  /**
   * If a panel contains the video-controls element, initialises
   * the VideoControls utility for that panel.
   * @param {HTMLElement} panel - The panel to check for video controls
   */
  initVideoControls(panel) {
    const videoControlsElement = panel.querySelector('.js--video-controls');
    if (videoControlsElement) {
      new VideoControls(videoControlsElement, panel);
    }
  }
}
